<template>
  <div
    class="replay-login"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('replayLoginTitle')"
    data-testid="replay-login-modal"
  >
    <div class="replay-login__context" data-testid="replay-login-context">
      <p class="replay-login__reason" data-testid="replay-login-reason">
        {{ reason }}
      </p>
      <ds-button
        variant="tertiary-deck"
        size="md"
        icon-only
        sheet
        :aria-label="$t('replayLoginClose')"
        data-testid="replay-login-close"
        @click="$emit('close')"
      >
        <span slot="icon"><ds-icon name="system-x" size="16" decorative /></span>
      </ds-button>
    </div>
    <div class="replay-login__body">
      <iframe
        v-if="frameSrc"
        class="replay-login__frame"
        :class="{ 'replay-login__frame--visible': isFrameReady }"
        :src="frameSrc"
        :title="$t('replayLoginTitle')"
        data-testid="replay-login-frame"
      />
      <div v-if="!isFrameReady" class="replay-login__ground">
        <div class="replay-login__card" :aria-busy="hasStalled ? 'false' : 'true'" data-testid="replay-login-loading-card">
          <div v-if="hasStalled" class="replay-login__stalled" role="alert">
            <p class="replay-login__stalled-text">
              {{ $t('replayLoginStalled') }}
            </p>
            <ds-button
              variant="primary"
              size="touch"
              data-testid="replay-login-retry"
              @click="retrySignIn"
            >
              {{ $t('replayLoginRetry') }}
            </ds-button>
          </div>
          <div v-else class="replay-login__skeleton" aria-hidden="true">
            <span class="replay-login__skeleton-bar" />
            <span class="replay-login__skeleton-bar" />
            <span class="replay-login__skeleton-bar" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cancelFrameSignIn, signInWithFrame } from '@ahaslides-product/plugins-auth'
import config from '@/config'
import UserApi from '@/api/user'
import util from '@/util'
import Sentry, { featureTags } from '@/3rd-parties/sentry'
import { loadAudienceDesignSystem } from '../../utils/designSystemLoader'
import DsButton from './RetakeDsButton'
import DsIcon from './RetakeDsIcon'
import retakeDeckTitle from './retakeDeckTitle'

const STALL_TIMEOUT_MS = 8000

export default {
  components: { DsButton, DsIcon },
  props: {
    redirect: { type: String, default: '' },
  },
  data() {
    return {
      frameSrc: null,
      isFrameReady: false,
      hasStalled: false,
      stallTimer: null,
    }
  },
  computed: {
    reason() {
      const title = retakeDeckTitle(this.$store.state.presentation)
      return title ? this.$t('replayLoginReason', { msg: title }) : this.$t('replayLoginReasonGeneric')
    },
  },
  mounted() {
    loadAudienceDesignSystem().catch((error) => {
      Sentry.captureException(error, {
        extra: { message: 'load design system failed' },
        tags: featureTags({ featureFlag: 'AHA-49118-replay', featureName: 'retake-sign-in', taskId: 'SEPA-24' }),
      })
    })
    document.addEventListener('keydown', this.closeOnEscape)
    this.startSignIn()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.closeOnEscape)
    clearTimeout(this.stallTimer)
    cancelFrameSignIn()
  },
  methods: {
    closeOnEscape(event) {
      if (event.key === 'Escape') this.$emit('close')
    },
    showStalled() {
      clearTimeout(this.stallTimer)
      cancelFrameSignIn()
      this.frameSrc = null
      this.isFrameReady = false
      this.hasStalled = true
      util.ga4Track('view_replay_login_stalled')
    },
    retrySignIn() {
      util.ga4Track('click_replay_login_retry')
      this.hasStalled = false
      this.startSignIn()
    },
    async startSignIn() {
      this.stallTimer = setTimeout(() => {
        if (!this.isFrameReady) this.showStalled()
      }, STALL_TIMEOUT_MS)
      let outcome
      try {
        outcome = await signInWithFrame({
          baseUrl: config.presenterUrl,
          dim: false,
          closable: false,
          // context=retake asks the auth form for its Retake heading and its sign-up action beside Google and Microsoft.
          // Google and Microsoft finish in their own window; the presenter app sends that window here if this tab is no longer listening.
          query: { context: 'retake', ...(this.redirect ? { redirect: this.redirect } : {}) },
          mount: (src) => {
            this.frameSrc = src
            return () => {
              this.frameSrc = null
            }
          },
          onReady: () => {
            this.isFrameReady = true
            clearTimeout(this.stallTimer)
          },
          onDone: this.confirmSession,
        })
      } catch (error) {
        Sentry.captureException(error, {
          tags: featureTags({ featureFlag: 'AHA-49118-replay', featureName: 'retake-sign-in', taskId: 'SEPA-22' }),
        })
        this.showStalled()
        return
      }
      const { status } = outcome
      if (status === 'authenticated') this.$emit('signed-in')
      else if (status === 'abandoned' && !this.hasStalled) this.$emit('close')
      else if (status === 'unresolved' || status === 'timeout') this.showStalled()
    },
    async confirmSession() {
      try {
        const user = await UserApi.getUser()
        if (!user || !user.id) return false
        await this.$store.dispatch('setCurrentUserAction', user)
        return true
      } catch (error) {
        return false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.replay-login {
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--aha-bg-overlay);

  &__context {
    display: flex;
    align-items: center;
    gap: theme('spacing.2');
    width: calc(100% - 2rem);
    max-width: 28rem;
    margin-top: theme('spacing.4');
    padding: theme('spacing.3') theme('spacing.3') theme('spacing.3') theme('spacing.5');
    box-sizing: border-box;
    background-color: theme('colors.base.0');
    border: 1px solid var(--aha-border-default);
    border-radius: theme('borderRadius.2xl');
    color: theme('colors.ds.base');
  }

  &__reason {
    flex: 1;
    margin: 0;
    font-size: theme('fontSize.base');
  }

  &__body {
    position: relative;
    flex: 1;
    width: 100%;
    min-height: 0;
  }

  &__frame {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    opacity: 0;

    &--visible {
      opacity: 1;
    }
  }

  &__ground {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: theme('spacing.4');
  }

  &__card {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 28rem;
    min-height: 12rem;
    box-sizing: border-box;
    padding: theme('spacing.6');
    background-color: theme('colors.base.0');
    border-radius: theme('borderRadius.2xl');
  }

  &__stalled {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: theme('spacing.4');
    text-align: center;
  }

  &__stalled-text {
    margin: 0;
    font-size: theme('fontSize.base');
    color: theme('colors.ds.base');
  }

  &__skeleton {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: theme('spacing.3');
  }

  &__skeleton-bar {
    height: theme('spacing.12');
    border-radius: theme('borderRadius.lg');
    background-color: theme('colors.secondary.10');
    animation: replay-login-pulse 1.2s ease-in-out infinite alternate;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
}

@keyframes replay-login-pulse {
  from { opacity: 1; }
  to { opacity: 0.5; }
}
</style>
